import { useRef, useEffect, useState } from 'react';
import { useGetParams } from './useGetParams';
import { useInterval } from './useInterval';
import { v4 as uuidv4 } from 'uuid';
import useLocalStorage from './useLocalStorage';
import { loadImages } from '../helpers/preload';
import { addMessageAndUpdateWinners, removeMessages } from '../helpers/authors';

export const useCarousel = (currentState) => {
  const {
    user,
    carousel = 1,
    startFrom,
    speedFast,
    speedSlow,
    iactiveIds,
    mode,
  } = useGetParams();

  const [showedMessages, setShowedMessages] = useState([]);

  const lastMessageIdRef = useRef(0);
  const nextMessageIndexRef = useRef(-1);
  const messagesRef = useRef([]);
  const currentMessageRef = useRef(null);
  const winnerMessageRef = useRef(null);

  const [winIds, setWinIds] = useLocalStorage('winIds', []);

  // время затраченное на появления одного сообщения в ms
  const [speed, setSpeed] = useState(500);

  const iactiveIdRef = useRef('');

  const protocol = window?.location.protocol || 'https:';

  const prevAuthors = useRef([]);
  const prevCurrentState = useRef('');

  const sizeWin = iactiveIds?.split(',')?.length || 1;
  const keyWinner = mode === 'allMessages' ? 'id' : 'senderNumber';

  useEffect(() => {
    if (prevCurrentState.current !== currentState) {
      const curState = currentState;

      let prevState = prevCurrentState.current;

      if (prevState === 'S6' && ['S4', 'S5'].includes(curState)) {
        setSpeed(speedSlow + 100);
        setTimeout(() => {
          setShowedMessages(
            showedMessages.filter((item) => !winIds.includes(item[keyWinner]))
          );
        }, 100);

        setTimeout(() => {
          setSpeed(curState === 'S4' ? speedSlow : speedFast);
        }, speedSlow + 100);

        prevCurrentState.current = currentState;
        return;
      }
      prevCurrentState.current = currentState;

      if (curState === 'S4' && speed !== speedSlow) {
        setSpeed(speedSlow);
      } else if (curState === 'S5' && speed !== speedFast) {
        setSpeed(speedFast);
      }

      if (curState === 'S6') {
        if (
          winnerMessageRef.current &&
          !winIds.includes(winnerMessageRef.current[keyWinner])
        ) {
          setWinIds([...winIds, winnerMessageRef.current[keyWinner]]);
          messagesRef.current = messagesRef.current.filter(
            (msg) =>
              !winIds.includes(msg[keyWinner]) &&
              msg[keyWinner] !== winnerMessageRef.current[keyWinner]
          );
        }
        setSpeed(null);
      }

      if (curState === 'S7') {
        setWinIds([]);
        setShowedMessages([]);
        nextMessageIndexRef.current = -1;
        messagesRef.current = [];
        prevAuthors.current = [];
        lastMessageIdRef.current = 0;
      }
    }
  }, [currentState]);

  const getBroadcastInfo = async () => {
    try {
      if (!user) {
        return;
      }

      const response = await fetch(
        `${protocol}//api.stream.iactive.pro/titreInfo?user=${user}&from=${
          lastMessageIdRef.current
        }&type=1${iactiveIds ? `&iactive_id=${iactiveIds}` : ''}`
      );
      const data = await response.json();
      if (!data) return;
      let { messagesList, deletedMessagesIdList, iactiveId } = data;

      // При запуске следующей трансляции обновление

      if (iactiveIdRef.current !== iactiveId) {
        lastMessageIdRef.current = 0;
        nextMessageIndexRef.current = -1;
        setShowedMessages([]);
        messagesRef.current = [];
        iactiveIdRef.current = iactiveId;
      }

      // если список сообщений пустой
      if (Array.isArray(messagesList) && messagesList.length === 0) {
        if (mode === 'onlyAllBroadcasts') {
          removeMessages(
            prevAuthors.current,
            messagesRef.current,
            deletedMessagesIdList,
            sizeWin
          );
        } else if (mode === 'allMessages') {
          if (
            messagesRef.current.some((el) =>
              deletedMessagesIdList.includes(+el.id)
            )
          ) {
            const newState = messagesRef.current.filter(
              (el) => !deletedMessagesIdList.includes(+el.id)
            );

            for (let i = 0; i < messagesRef.current.length; i++) {
              if (
                !newState.find(
                  (el) =>
                    nextMessageIndexRef.current &&
                    el.id ===
                      messagesRef.current[nextMessageIndexRef.current]?.id
                )
              ) {
                if (
                  messagesRef.current[nextMessageIndexRef.current] ===
                  messagesRef.current[messagesRef.current.length - 1]
                ) {
                  nextMessageIndexRef.current--;
                  if (nextMessageIndexRef.current < 0)
                    nextMessageIndexRef.current = 0;
                }
              }
              if (
                i < nextMessageIndexRef.current &&
                !newState.find((el) => el.id === messagesRef.current[i].id)
              ) {
                nextMessageIndexRef.current--;
                if (nextMessageIndexRef.current < 0)
                  nextMessageIndexRef.current = 0;
              }
            }
            messagesRef.current = newState;
          }
        }

        if (messagesRef.current.length === 0) {
          setShowedMessages([]);
        }

        // console.log('Все авторы:', prevAuthors.current);
        // console.log('Авторы-победители:', messagesRef.current);

        //запоминаем id последнего сообщения, для дальнейших запросов и обнуляем при переключении интерактивов
        if (iactiveIdRef.current && iactiveIdRef.current !== iactiveId) {
          lastMessageIdRef.current = 0;
          iactiveIdRef.current = iactiveId;
          return;
        } else {
          iactiveIdRef.current = iactiveId;
          lastMessageIdRef.current =
            messagesList[0]?.id || lastMessageIdRef.current;
        }
        return;
      }

      // Удаляем сообщения, если они не содержат текста или картинки.
      messagesList = messagesList.filter(
        (el) =>
          !(
            Array.isArray(el.message.attachments) &&
            el.message.attachments.length > 0 &&
            typeof el.message.attachments[0] === 'object' &&
            el.message.attachments[0].type === 'audio_msg' &&
            el.message.content === ''
          )
      );

      //запоминаем id последнего сообщения, для дальнейших запросов и обнуляем при переключении интерактивов
      if (iactiveIdRef.current && iactiveIdRef.current !== iactiveId) {
        lastMessageIdRef.current = 0;
        iactiveIdRef.current = iactiveId;
        return;
      } else {
        iactiveIdRef.current = iactiveId;
        lastMessageIdRef.current =
          messagesList[0]?.id || lastMessageIdRef.current;
      }

      messagesList = messagesList?.filter(
        (el) => el.message.content && el.titrTag === 'F2'
      );

      // предзагрузка аватарок
      let urls = messagesList?.map((m) => m.message.image)?.filter((u) => u);
      if (urls && urls.length !== 0) {
        loadImages(urls);
      }

      if (mode === 'onlyAllBroadcasts') {
        // Обрабатываем каждое сообщение
        messagesList.forEach((msg) => {
          addMessageAndUpdateWinners(
            prevAuthors.current,
            messagesRef.current,
            msg,
            sizeWin
          );
        });
      } else if (mode === 'allMessages') {
        const res = messagesList.filter(
          (m) => !messagesRef.current.find((el) => el.id === m.id)
        );

        messagesRef.current = [...messagesRef.current, ...res];
      }

      // console.log('Все авторы:', prevAuthors.current);
      // console.log('Авторы-победители:', messagesRef.current);
    } catch (error) {
      console.error(error);
    }
  };

  const iterateIndex = (array) => {
    if (carousel === '1') {
      if (!array.length) {
        nextMessageIndexRef.current = -1;
        return;
      }
      if (nextMessageIndexRef.current < array.length - 1) {
        nextMessageIndexRef.current++;
      } else {
        if (messagesRef.current.length >= startFrom) {
          messagesRef.current = messagesRef.current;

          nextMessageIndexRef.current = 0;
        }
      }
    } else {
      if (nextMessageIndexRef.current < messagesRef.current.length - 1)
        nextMessageIndexRef.current++;
      else {
        nextMessageIndexRef.current = messagesRef.current.length - 1;
      }
    }
  };

  const updateShowedMessages = (newMessages) => {
    if (newMessages && messagesRef.current.length >= startFrom) {
      currentMessageRef.current = newMessages;
      const currentMessageWithUUID = { ...newMessages, UUID: uuidv4() };

      const newMessagesArr = [currentMessageWithUUID, ...showedMessages].slice(
        0,
        4
      );
      winnerMessageRef.current = newMessagesArr[1];
      setShowedMessages(newMessagesArr);
    }
  };

  useEffect(() => {
    getBroadcastInfo().then(() => {
      if (messagesRef.current && messagesRef.current.length > 0) {
        iterateIndex(messagesRef.current);
        updateShowedMessages(messagesRef.current[nextMessageIndexRef.current]);
      }
    });
  }, []);

  // Выбор сообщения для показа
  useInterval(
    () => {
      iterateIndex(messagesRef.current);
      if (carousel === '1' && messagesRef.current.length < startFrom) {
        return;
      } else {
        updateShowedMessages(messagesRef.current[nextMessageIndexRef.current]);
      }
    },
    currentState === 'S4' || currentState === 'S5' ? speed : null
  );

  // Получение информации о титре
  useInterval(getBroadcastInfo, 2000);

  return {
    currentState,
    showedMessages,
    messagesRef: messagesRef.current,
    winnerMessage: winnerMessageRef.current,
    speed,
    mode,
    keyWinner,
  };
};
